package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.CheckBox
import androidx.compose.material.icons.filled.CheckBoxOutlineBlank
import androidx.compose.material.icons.filled.RadioButtonChecked
import androidx.compose.material.icons.filled.RadioButtonUnchecked
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.explain.Breakdown
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.schedule.AssignCandidate
import com.evoapps.evoshift.domain.schedule.AssignCandidates
import com.evoapps.evoshift.domain.schedule.DayAnswer
import com.evoapps.evoshift.domain.usecase.shift.ShiftFactory
import com.evoapps.evoshift.domain.util.calculateDurationHours
import com.evoapps.evoshift.domain.util.calculateDurationMinutes
import com.evoapps.evoshift.domain.util.plannedTimeRange
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.theme.MotionContent
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.ui.util.durationText
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.ui.util.templateDurationText
import com.evoapps.evoshift.viewmodels.RosterDetailUiState
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.DialogSectionLabel
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoPrimaryButton
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.OnDialog
import com.evoapps.evoshift.widgets.RoleField
import com.evoapps.evoshift.widgets.parseClockTime
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.add_shift_add_another
import evoshift.composeapp.generated.resources.add_shift_add_selected
import evoshift.composeapp.generated.resources.add_shift_assign
import evoshift.composeapp.generated.resources.add_shift_conflict_warning
import evoshift.composeapp.generated.resources.add_shift_custom
import evoshift.composeapp.generated.resources.add_shift_dialog_title
import evoshift.composeapp.generated.resources.add_shift_duration
import evoshift.composeapp.generated.resources.add_shift_next_day
import evoshift.composeapp.generated.resources.add_shift_nobody
import evoshift.composeapp.generated.resources.add_shift_nothing
import evoshift.composeapp.generated.resources.add_shift_pick_several
import evoshift.composeapp.generated.resources.add_shift_pick_time
import evoshift.composeapp.generated.resources.add_shift_unavailable_warning
import evoshift.composeapp.generated.resources.busy_elsewhere_other_employer
import evoshift.composeapp.generated.resources.employee_from_other_workplaces
import evoshift.composeapp.generated.resources.roster_picker_away
import evoshift.composeapp.generated.resources.roster_picker_guest_from
import evoshift.composeapp.generated.resources.roster_picker_guests_hint
import evoshift.composeapp.generated.resources.busy_elsewhere_same_owner
import evoshift.composeapp.generated.resources.roster_cancel_addition
import evoshift.composeapp.generated.resources.roster_day_add_shift
import evoshift.composeapp.generated.resources.roster_day_add_shift_hint
import evoshift.composeapp.generated.resources.roster_from
import evoshift.composeapp.generated.resources.roster_from_templates
import evoshift.composeapp.generated.resources.roster_grid_added
import evoshift.composeapp.generated.resources.roster_picker_absent
import evoshift.composeapp.generated.resources.roster_picker_same_day
import evoshift.composeapp.generated.resources.roster_to
import evoshift.composeapp.generated.resources.time_format_invalid
import evoshift.composeapp.generated.resources.time_same_start_end
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/*
 * SH-204: "Add shift" in the day's sheet or pane. On wide windows (the width classes above
 * COMPACT: tablets, desktop, web) it opens a centred dialog "New shift: Tuesday, 3 February"; on
 * phones the same parts open in the day's bottom sheet. The parts: the workplace's shift templates
 * as cards (one tap adds; "Pick several" ticks several, added at once), a custom shift (start and
 * end in 15-minute steps or typed, over midnight allowed, its length and cost as typed, a role), and
 * optionally a person, with their answer for the day and what stands in the way. Every save goes
 * through the builder's view model (RosterDetailViewModel.addShiftsToDay: the create-shift use
 * case, shown at once, one Undo).
 */

/** Test tag of the wide windows' "New shift" dialog content. */
const val ADD_SHIFT_DIALOG_TAG = "add_shift_dialog"

/** Test tag of the phone's "Add shift" form in the day's sheet. */
const val ADD_SHIFT_SHEET_TAG = "add_shift_sheet"

/** Test tag of a template card (by template id). */
fun addShiftTemplateTag(templateId: String) = "add_shift_template_$templateId"

/** Test tag of a person's row in "Assign a person" (null: nobody). */
fun addShiftPersonTag(userId: String?) = "add_shift_person_${userId ?: "nobody"}"

/** Test tag of the custom shift's length line. */
const val ADD_SHIFT_DURATION_TAG = "add_shift_duration"

/** Test tags of the phone form's buttons (the dialog's are EvoDialogTags.CONFIRM / NEUTRAL). */
const val ADD_SHIFT_CONFIRM_TAG = "add_shift_confirm"
const val ADD_SHIFT_ANOTHER_TAG = "add_shift_another"

/**
 * SH-204: what the add-shift dialog needs beyond [DayShiftEditing]: the day, the save (templates
 * for a person or nobody), the people for the shifts being added and their cost.
 */
class AddShiftSupport(
    val date: LocalDate,
    /** Adds [templates] (custom times are a template with id [CUSTOM_TEMPLATE_ID]) for [userId] or nobody. */
    val onAdd: (templates: List<ShiftTemplate>, userId: String?) -> Unit,
    /** The team for the shifts being added (their answer, absences, overlaps); [AddShiftCandidates]. */
    val candidates: (drafts: List<ShiftTemplate>) -> List<AssignCandidate>,
    val availabilitySlots: List<AvailabilitySlot>,
    /** The estimated employer cost of [hours] worked by [userId] (null: nobody); null: no money role. */
    val costFor: ((userId: String?, hours: Double) -> Breakdown?)? = null,
    /** SH-267: people of the group's other workplaces to borrow for the shifts ("From your other workplaces"). */
    val guests: (drafts: List<ShiftTemplate>) -> List<AssignCandidate> = { emptyList() }
)

/** The id of a custom shift's template (as the builder's other custom shifts). */
const val CUSTOM_TEMPLATE_ID = "custom"

/**
 * SH-204: the form behind the dialog (and the phone's sheet): the ticked templates, the custom
 * times and role, the person, and what is wrong once Add was tried. Plain state, no composition:
 * the view-model tests drive it directly.
 */
class AddShiftFormState {
    var multiSelect by mutableStateOf(false)
        private set
    /** Ticked template ids, in the order they were ticked. */
    var ticked by mutableStateOf<List<String>>(emptyList())
        private set
    var start by mutableStateOf("")
    var end by mutableStateOf("")
    var role by mutableStateOf("")
    /** The person the shifts go to; null: nobody (unassigned, as before SH-204). */
    var userId by mutableStateOf<String?>(null)
    /** Add was tried: the custom part's errors show. */
    var tried by mutableStateOf(false)
        private set
    /** How many shifts "Add and create another" added last (shown in the dialog); 0: none yet. */
    var addedCount by mutableStateOf(0)
        private set

    val startTime: String? get() = parseClockTime(start)
    val endTime: String? get() = parseClockTime(end)

    /** Something was typed in the custom part (it then has to be whole). */
    val customStarted: Boolean get() = start.isNotBlank() || end.isNotBlank()
    val sameTimes: Boolean get() = startTime != null && startTime == endTime
    val customValid: Boolean get() = startTime != null && endTime != null && !sameTimes

    /** The custom shift's length in minutes (over midnight counts into the next day); null until valid. */
    val durationMinutes: Int? get() = if (customValid) calculateDurationMinutes(startTime!!, endTime!!) else null

    /** The custom shift ends the next day (22:00–06:00). */
    val overnight: Boolean get() = customValid && endTime!! < startTime!!

    /** Add was tried with no template ticked and no custom times. */
    val nothingChosen: Boolean get() = tried && !customStarted && ticked.isEmpty()

    fun toggleMultiSelect() {
        multiSelect = !multiSelect
        if (!multiSelect) ticked = emptyList()
    }

    fun toggle(templateId: String) {
        ticked = if (templateId in ticked) ticked - templateId else ticked + templateId
    }

    /** The custom times and role as a template; null while they are not valid. */
    fun customTemplate(): ShiftTemplate? {
        if (!customValid) return null
        val role = role.trim().ifBlank { null }
        return ShiftTemplate(id = CUSTOM_TEMPLATE_ID, name = role.orEmpty(), startTime = startTime!!, endTime = endTime!!, role = role, skills = emptyList())
    }

    /** The shifts being put together (the ticked templates, then the custom one), for the people's conflicts. */
    fun drafts(templates: List<ShiftTemplate>): List<ShiftTemplate> =
        ticked.mapNotNull { id -> templates.firstOrNull { it.id == id } } + listOfNotNull(customTemplate())

    /**
     * What Add saves: the ticked templates and the custom shift (when anything was typed in it, it
     * must be whole); null, with the errors shown, when there is nothing to add or the times are wrong.
     */
    fun submission(templates: List<ShiftTemplate>): List<ShiftTemplate>? {
        tried = true
        val picked = ticked.mapNotNull { id -> templates.firstOrNull { it.id == id } }
        if (customStarted) {
            val custom = customTemplate() ?: return null
            return picked + custom
        }
        return picked.ifEmpty { null }
    }

    /** "Add and create another": the custom part and the ticks start again; the person stays. */
    fun resetForAnother(added: Int) {
        start = ""
        end = ""
        role = ""
        ticked = emptyList()
        tried = false
        addedCount = added
    }
}

/** 00:00, 00:15 … 23:45: the time pickers' steps. */
internal val QuarterHours: List<String> = (0 until 24 * 4).map { i ->
    "${(i / 4).toString().padStart(2, '0')}:${((i % 4) * 15).toString().padStart(2, '0')}"
}

/**
 * SH-204: the team for the shifts being added on [date] ([drafts]; none yet: only their answers
 * and absences), as the day's person picker ranks them (AssignCandidates), the whole team (any
 * position): someone overlapping any of the drafts counts as overlapping.
 */
internal fun RosterDetailUiState.addShiftCandidates(date: LocalDate, drafts: List<ShiftTemplate>): List<AssignCandidate> {
    val rosterId = roster?.id ?: return emptyList()
    val workplaceId = roster.workplaceId
    val day = date.day
    val probes: List<Shift> = drafts.ifEmpty { listOf(null) }.map { draft ->
        val base = ShiftFactory.newShift(rosterId, date, draft?.startTime ?: "00:00", draft?.endTime ?: "00:00", role = null, createdBy = "", now = "")
            .copy(id = "draft")
        if (draft == null) base.copy(plannedTime = TimeRange("", "")) else base.copy(plannedTime = plannedTimeRange(date, draft.startTime, draft.endTime))
    }
    val preferences = availabilityByUserId.mapNotNull { (uid, a) -> a.days[day]?.let { uid to it } }.toMap()
    val busy = busyElsewhereByUserAndDate.mapNotNull { (uid, days) -> days[date]?.let { uid to it } }.toMap()
    val ranked = probes.map { probe ->
        AssignCandidates.rank(
            shift = probe, team = availableEmployees, workplaceId = workplaceId, dayShifts = shiftsByDate[date].orEmpty(),
            preferences = preferences, plannedHours = plannedHoursByUserId, targetHours = targetHoursByUserId,
            absences = absences, busyElsewhere = busy
        ).associateBy { it.user.uid }
    }
    return ranked.first().values
        .map { first ->
            val all = ranked.mapNotNull { it[first.user.uid] }
            first.copy(overlaps = all.any { it.overlaps }, overTarget = drafts.isNotEmpty() && all.any { it.overTarget })
        }
        .sortedWith(compareBy<AssignCandidate> { it.hasConflict }.thenBy { it.answer.ordinal }.thenBy { it.user.displayName.lowercase() })
}

/**
 * SH-267: the people of the group's other workplaces for the shifts being added on [date]
 * ("From your other workplaces"), ranked as [addShiftCandidates] ranks the team.
 */
internal fun RosterDetailUiState.addShiftGuestCandidates(date: LocalDate, drafts: List<ShiftTemplate>): List<AssignCandidate> {
    val rosterId = roster?.id ?: return emptyList()
    if (borrowCandidates.isEmpty()) return emptyList()
    val probes: List<Shift> = drafts.ifEmpty { listOf(null) }.map { draft ->
        val base = ShiftFactory.newShift(rosterId, date, draft?.startTime ?: "00:00", draft?.endTime ?: "00:00", role = null, createdBy = "", now = "")
            .copy(id = "draft")
        if (draft == null) base.copy(plannedTime = TimeRange("", "")) else base.copy(plannedTime = plannedTimeRange(date, draft.startTime, draft.endTime))
    }
    val busy = busyElsewhereByUserAndDate.mapNotNull { (uid, days) -> days[date]?.let { uid to it } }.toMap()
    val ranked = probes.map { probe ->
        AssignCandidates.rankGuests(probe, borrowCandidates, roster.workplaceId, shiftsByDate[date].orEmpty(), busy).associateBy { it.user.uid }
    }
    return ranked.first().values
        .map { first -> first.copy(overlaps = ranked.mapNotNull { it[first.user.uid] }.any { it.overlaps }) }
        .sortedWith(compareBy<AssignCandidate> { it.hasConflict }.thenBy { it.answer.ordinal }.thenBy { it.user.displayName.lowercase() })
}

/**
 * SH-204: "Add shift" in the day's details: on wide windows the button opens [AddShiftDialog];
 * on phones the same form opens in place in the sheet ([AddShiftForm]).
 */
@Composable
internal fun AddShiftEntry(editing: DayShiftEditing) {
    val wide = !LocalWindowWidthClass.current.isCompact && editing.adding != null
    var open by remember { mutableStateOf(false) }
    val addButton = @Composable {
        EvoSecondaryButton(
            DialogAction(stringResource(Res.string.roster_day_add_shift), { open = true }, icon = Icons.Default.Add),
            Modifier.fillMaxWidth()
        )
    }
    if (wide) {
        addButton()
        if (open) AddShiftDialog(editing, editing.adding!!, onDismiss = { open = false })
        return
    }
    // SH-158: "Add shift" gives way to the form (and back) by fading.
    MotionContent(open, Modifier.fillMaxWidth(), label = "addShift") { shown ->
        if (!shown) {
            addButton()
        } else {
            val form = remember { AddShiftFormState() }
            Column(Modifier.fillMaxWidth().testTag(ADD_SHIFT_SHEET_TAG), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                AddShiftForm(editing, editing.adding, form, onAdded = { open = false })
                val add = { another: Boolean -> submit(editing, form, another) { open = false } }
                EvoPrimaryButton(
                    DialogAction(confirmLabel(form), { add(false) }),
                    Modifier.fillMaxWidth().testTag(ADD_SHIFT_CONFIRM_TAG)
                )
                if (editing.adding != null) {
                    EvoSecondaryButton(
                        DialogAction(stringResource(Res.string.add_shift_add_another), { add(true) }),
                        Modifier.fillMaxWidth().testTag(ADD_SHIFT_ANOTHER_TAG)
                    )
                }
                EvoSecondaryButton(DialogAction(stringResource(Res.string.roster_cancel_addition), { open = false }), Modifier.fillMaxWidth())
            }
        }
    }
}

/** "New shift: Tuesday, 3 February": the form in a centred dialog (wide windows). */
@Composable
internal fun AddShiftDialog(editing: DayShiftEditing, adding: AddShiftSupport, onDismiss: () -> Unit) {
    val form = remember { AddShiftFormState() }
    EvoDialog(
        title = stringResource(Res.string.add_shift_dialog_title, dayAndDate(adding.date)),
        icon = Icons.Default.Add,
        onDismiss = onDismiss,
        width = DialogWidth.Wide,
        confirm = DialogAction(confirmLabel(form), { submit(editing, form, another = false, close = onDismiss) }),
        neutral = DialogAction(stringResource(Res.string.add_shift_add_another), { submit(editing, form, another = true, close = onDismiss) })
    ) {
        Column(Modifier.fillMaxWidth().testTag(ADD_SHIFT_DIALOG_TAG), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            AddShiftForm(editing, adding, form, onAdded = onDismiss)
        }
    }
}

@Composable
private fun confirmLabel(form: AddShiftFormState): String =
    if (form.ticked.isNotEmpty() && !form.customStarted) stringResource(Res.string.add_shift_add_selected, form.ticked.size)
    else stringResource(Res.string.roster_day_add_shift)

/** Saves the form's shifts; [close] after Add, the custom part reset after "Add and create another". */
private fun submit(editing: DayShiftEditing, form: AddShiftFormState, another: Boolean, close: () -> Unit) {
    val templates = form.submission(editing.templates) ?: return
    add(editing, templates, form.userId)
    if (another) form.resetForAnother(templates.size) else close()
}

/** Through [AddShiftSupport.onAdd]; without it (no day known) through the day's older callbacks, nobody assigned. */
private fun add(editing: DayShiftEditing, templates: List<ShiftTemplate>, userId: String?) {
    val adding = editing.adding
    if (adding != null) {
        adding.onAdd(templates, userId)
        return
    }
    templates.forEach { t ->
        if (t.id == CUSTOM_TEMPLATE_ID) editing.onAddCustom(t.startTime, t.endTime, t.role) else editing.onAddFromTemplate(t)
    }
}

/** The form's parts: the hint, the templates, the custom shift, the person (when [adding] knows the team). */
@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun AddShiftForm(editing: DayShiftEditing, adding: AddShiftSupport?, form: AddShiftFormState, onAdded: () -> Unit) {
    val on = OnDialog
    if (form.userId == null) {
        Text(stringResource(Res.string.roster_day_add_shift_hint), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
    }
    if (form.addedCount > 0) {
        Text(stringResource(Res.string.roster_grid_added, form.addedCount), style = MaterialTheme.typography.bodyMedium, color = on.success, fontWeight = FontWeight.Bold)
    }
    val costFor: ((Double) -> Breakdown?)? = adding?.costFor?.let { cost -> { hours -> cost(form.userId, hours) } } ?: editing.shiftCost

    if (editing.templates.isNotEmpty()) {
        DialogSection(stringResource(Res.string.roster_from_templates)) {
            TickRow(stringResource(Res.string.add_shift_pick_several), form.multiSelect, onToggle = form::toggleMultiSelect)
            FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                editing.templates.forEach { template ->
                    val ticked = template.id in form.ticked
                    TemplateCard(
                        template = template,
                        ticked = ticked.takeIf { form.multiSelect },
                        cost = costFor?.invoke(calculateDurationHours(template.startTime, template.endTime)),
                        editing = editing,
                        onClick = {
                            if (form.multiSelect) {
                                form.toggle(template.id)
                            } else {
                                add(editing, listOf(template), form.userId)
                                onAdded()
                            }
                        }
                    )
                }
            }
        }
    }

    DialogSection(stringResource(Res.string.add_shift_custom)) {
        val customErrors = form.tried && form.customStarted
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            QuarterTimeField(form.start, { form.start = it }, stringResource(Res.string.roster_from), "08:00", showErrors = customErrors, modifier = Modifier.weight(1f))
            QuarterTimeField(
                form.end, { form.end = it }, stringResource(Res.string.roster_to), "16:00", showErrors = customErrors,
                error = stringResource(Res.string.time_same_start_end).takeIf { form.sameTimes },
                modifier = Modifier.weight(1f)
            )
        }
        form.durationMinutes?.let { minutes ->
            val length = stringResource(Res.string.add_shift_duration, durationText(minutes))
            val text = if (form.overnight) "$length · ${stringResource(Res.string.add_shift_next_day)}" else length
            Text(text, style = MaterialTheme.typography.bodyMedium, color = on.onSurface, modifier = Modifier.testTag(ADD_SHIFT_DURATION_TAG))
        }
        RoleField(role = form.role, onRoleChange = { form.role = it }, availableRoles = editing.availableRoles, optional = true, imeAction = ImeAction.Done)
        form.durationMinutes?.let { minutes ->
            val hours = minutes / 60.0
            costFor?.invoke(hours)?.let { ShiftCostLine(it, editing.currency) }
            editing.budgetEffect?.invoke(hours)?.let { BudgetEffectLines(it, editing.currency) }
        }
        if (form.nothingChosen) {
            Text(stringResource(Res.string.add_shift_nothing), style = MaterialTheme.typography.bodySmall, color = on.error, fontWeight = FontWeight.Bold)
        }
    }

    if (adding != null) PersonChoice(adding, editing, form)
}

/** "Pick several": a tick box whose whole row toggles it. */
@Composable
private fun TickRow(label: String, checked: Boolean, onToggle: () -> Unit) {
    val on = OnDialog
    Row(
        Modifier.heightIn(min = 40.dp).clip(RoundedCornerShape(8.dp)).handCursor()
            .toggleable(value = checked, role = Role.Checkbox, onValueChange = { onToggle() })
            .padding(end = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(if (checked) Icons.Default.CheckBox else Icons.Default.CheckBoxOutlineBlank, null, tint = on.accent)
        Spacer(Modifier.width(8.dp))
        Text(label, style = MaterialTheme.typography.bodyMedium, color = on.onSurface)
    }
}

/** A template as a card: "Morning · 06:30–14:00", its length and cost; [ticked] null: a tap adds it. */
@Composable
private fun TemplateCard(template: ShiftTemplate, ticked: Boolean?, cost: Breakdown?, editing: DayShiftEditing, onClick: () -> Unit) {
    val on = OnDialog
    val shape = RoundedCornerShape(12.dp)
    val base = Modifier
        .testTag(addShiftTemplateTag(template.id))
        .clip(shape)
        .border(if (ticked == true) 2.dp else 1.dp, on.accent.copy(alpha = if (ticked == true) 1f else 0.6f), shape)
        .background(on.accent.copy(alpha = if (ticked == true) 0.08f else 0f))
        .handCursor()
    val clickable = if (ticked != null) base.toggleable(value = ticked, role = Role.Checkbox, onValueChange = { onClick() }) else base.clickable(onClick = onClick)
    Box(clickable.padding(horizontal = 14.dp, vertical = 10.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (ticked != null) {
                Icon(if (ticked) Icons.Default.CheckBox else Icons.Default.CheckBoxOutlineBlank, null, tint = on.accent, modifier = Modifier.size(20.dp))
                Spacer(Modifier.width(8.dp))
            }
            Column {
                Text("${template.name} · ${template.startTime.take(5)}–${template.endTime.take(5)}", style = MaterialTheme.typography.bodySmall, color = on.onSurface)
                Text(templateDurationText(template.startTime, template.endTime), style = MaterialTheme.typography.labelSmall, color = on.onSurfaceVariant)
                cost?.let { ShiftCostLine(it, editing.currency, explain = false) }
                // SH-115: what the shift adds to the budget.
                editing.budgetEffect?.invoke(calculateDurationHours(template.startTime, template.endTime))?.let { effect ->
                    Text(
                        formatMoneyDelta(effect.delta, editing.currency),
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = if (effect.isOver) on.error else on.accent
                    )
                }
            }
        }
    }
}

/**
 * A clock time typed (`8`, `830`, `22:00`) or picked from the clock button's list in 15-minute
 * steps (opened near the time already there).
 */
@Composable
private fun QuarterTimeField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholder: String,
    showErrors: Boolean,
    modifier: Modifier = Modifier,
    error: String? = null
) {
    var picking by remember { mutableStateOf(false) }
    val invalid = if (value.isBlank()) showErrors else parseClockTime(value) == null
    Box(modifier) {
        EvoTextField(
            value = value,
            onValueChange = { new -> onValueChange(new.filter { it.isDigit() || it == ':' }.take(5)) },
            label = label,
            placeholder = placeholder,
            error = error ?: stringResource(Res.string.time_format_invalid).takeIf { invalid },
            keyboardType = KeyboardType.Number,
            trailingIcon = {
                IconButtonWithTooltip(onClick = { picking = true }, modifier = Modifier.handCursor(), tooltip = "${stringResource(Res.string.add_shift_pick_time)}: $label") {
                    Icon(Icons.Default.Schedule, contentDescription = "${stringResource(Res.string.add_shift_pick_time)}: $label")
                }
            }
        )
        val scroll = rememberScrollState()
        DropdownMenu(expanded = picking, onDismissRequest = { picking = false }, scrollState = scroll, modifier = Modifier.heightIn(max = 280.dp)) {
            QuarterHours.forEach { time ->
                DropdownMenuItem(text = { Text(time) }, onClick = {
                    onValueChange(time)
                    picking = false
                })
            }
        }
        // Opens near the time typed (or the placeholder), not at midnight.
        val near = parseClockTime(value) ?: placeholder
        val index = QuarterHours.indexOfFirst { it >= near }.coerceAtLeast(0)
        val itemPx = with(LocalDensity.current) { 48.dp.roundToPx() }
        LaunchedEffect(picking) { if (picking) scroll.scrollTo((index - 2).coerceAtLeast(0) * itemPx) }
    }
}

/** "Assign a person (optional)": nobody (the default), or one of the team with their day. */
@Composable
private fun PersonChoice(adding: AddShiftSupport, editing: DayShiftEditing, form: AddShiftFormState) {
    val candidates = adding.candidates(form.drafts(editing.templates))
    DialogSection(stringResource(Res.string.add_shift_assign)) {
        PersonRow(null, form.userId == null, adding, onSelect = { form.userId = null })
        candidates.forEach { candidate ->
            PersonRow(candidate, form.userId == candidate.user.uid, adding, onSelect = { form.userId = candidate.user.uid })
        }
        // SH-267: people of the group's other workplaces, each with where they come from.
        val guests = adding.guests(form.drafts(editing.templates))
        if (guests.isNotEmpty()) {
            DialogSectionLabel(stringResource(Res.string.employee_from_other_workplaces))
            Text(stringResource(Res.string.roster_picker_guests_hint), style = MaterialTheme.typography.labelSmall, color = OnDialog.onSurfaceVariant)
            guests.forEach { candidate ->
                PersonRow(candidate, form.userId == candidate.user.uid, adding, onSelect = { form.userId = candidate.user.uid })
            }
        }
        (candidates + guests).firstOrNull { it.user.uid == form.userId }?.let { chosen ->
            val on = OnDialog
            val warning = when {
                chosen.hasConflict -> stringResource(Res.string.add_shift_conflict_warning, chosen.user.displayName)
                chosen.answer == DayAnswer.UNAVAILABLE -> stringResource(Res.string.add_shift_unavailable_warning, chosen.user.displayName)
                else -> null
            }
            warning?.let {
                Row(verticalAlignment = Alignment.Top) {
                    Icon(Icons.Default.Warning, null, tint = on.warning, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text(it, style = MaterialTheme.typography.bodySmall, color = on.warning, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

@Composable
private fun PersonRow(candidate: AssignCandidate?, selected: Boolean, adding: AddShiftSupport, onSelect: () -> Unit) {
    val on = OnDialog
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .testTag(addShiftPersonTag(candidate?.user?.uid))
            .clip(RoundedCornerShape(12.dp))
            // The pick is marked by its edge: a lighter fill would take the coloured lines under 4.5:1.
            .border(if (selected) 2.dp else 1.dp, if (selected) on.accent else on.onSurface.copy(alpha = 0.12f), RoundedCornerShape(12.dp))
            .background(on.onSurface.copy(alpha = 0.04f))
            .handCursor()
            .selectable(selected = selected, role = Role.RadioButton, onClick = onSelect)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(if (selected) Icons.Default.RadioButtonChecked else Icons.Default.RadioButtonUnchecked, null, tint = on.accent, modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(12.dp))
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            if (candidate == null) {
                Text(stringResource(Res.string.add_shift_nobody), style = MaterialTheme.typography.titleSmall, color = on.onSurface)
                return@Column
            }
            Text(candidate.user.displayName, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = on.onSurface)
            candidate.guestFrom?.let { from ->
                Text(stringResource(Res.string.roster_picker_guest_from, from.workplaceName), style = MaterialTheme.typography.labelSmall, color = on.accent)
            }
            val absence = candidate.absence
            if (candidate.awayAtHome) {
                Text(stringResource(Res.string.roster_picker_away), style = MaterialTheme.typography.labelSmall, color = on.error, fontWeight = FontWeight.Bold)
            } else if (absence != null) {
                Text(stringResource(Res.string.roster_picker_absent, absence.type.label()), style = MaterialTheme.typography.labelSmall, color = on.error, fontWeight = FontWeight.Bold)
            } else {
                AvailabilityLine(candidate.preference, adding.availabilitySlots)
            }
            candidate.busyElsewhere.forEach { busy ->
                val where = if (busy.sameOwner) {
                    "${stringResource(Res.string.busy_elsewhere_same_owner)} · ${listOfNotNull(busy.workplaceName, busyTimes(busy)).joinToString(" ")}"
                } else {
                    "${stringResource(Res.string.busy_elsewhere_other_employer)} · ${busyTimes(busy)}"
                }
                Text(where, style = MaterialTheme.typography.labelSmall, color = on.accent)
            }
            if (candidate.sameDayShifts.isNotEmpty()) {
                Text(
                    stringResource(Res.string.roster_picker_same_day, candidate.sameDayShifts.joinToString(", ") { it.plannedRange() }),
                    style = MaterialTheme.typography.labelSmall,
                    color = if (candidate.overlaps) on.error else on.onSurfaceVariant,
                    fontWeight = if (candidate.overlaps) FontWeight.Bold else null
                )
            }
        }
        if (candidate?.hasConflict == true) Icon(Icons.Default.Warning, null, tint = on.error, modifier = Modifier.size(18.dp))
    }
}
